// Empire Apps Chassis · connectors/financial/upi-export-parser
// CSV / PDF export from Google Pay, PhonePe, Paytm — parse into BankTransaction list.
// Each provider exports a slightly different schema; this parser auto-detects.

import {
  type BankTransaction,
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  accountId,
  fail,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';
import { getPdfBridge } from './adobe-pdf-bridge.ts';

const STATUS_KEY = 'empire:financial:upi-export:status';

interface PersistedStatus {
  readonly connected: boolean;
  readonly lastSync: string | null;
  readonly importedCount: number;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, lastSync: null, importedCount: 0 });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

export type UpiProvider = 'gpay' | 'phonepe' | 'paytm' | 'unknown';

export function detectProvider(headers: readonly string[]): UpiProvider {
  const joined = headers.join(' ').toLowerCase();
  if (joined.includes('google pay') || joined.includes('gpay')) return 'gpay';
  if (joined.includes('phonepe')) return 'phonepe';
  if (joined.includes('paytm')) return 'paytm';
  return 'unknown';
}

export interface UpiParseResult {
  readonly provider: UpiProvider;
  readonly transactions: readonly BankTransaction[];
}

function parseDate(s: string): string {
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10) + 'T00:00:00.000Z';
  const m = t.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{2,4})/);
  if (m) {
    const dd = (m[1] ?? '01').padStart(2, '0');
    const mm = (m[2] ?? '01').padStart(2, '0');
    let yy = m[3] ?? '00';
    if (yy.length === 2) yy = `20${yy}`;
    return `${yy}-${mm}-${dd}T00:00:00.000Z`;
  }
  return new Date().toISOString();
}

function csvRows(text: string): readonly (readonly string[])[] {
  const out: string[][] = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const fields: string[] = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') {
        inQ = !inQ;
      } else if (c === ',' && !inQ) {
        fields.push(cur);
        cur = '';
      } else {
        cur += c;
      }
    }
    fields.push(cur);
    out.push(fields);
  }
  return out;
}

export function parseUpiCsv(text: string): UpiParseResult {
  const rows = csvRows(text);
  const headers = rows[0] ?? [];
  const provider = detectProvider(headers);
  const headerMap: Record<string, number> = {};
  headers.forEach((h, i) => {
    headerMap[h.toLowerCase().trim()] = i;
  });
  const dateIdx = pickIdx(headerMap, ['date', 'transaction date', 'txn date', 'time']);
  const amtIdx = pickIdx(headerMap, ['amount', 'amount (inr)', 'inr amount']);
  const merchIdx = pickIdx(headerMap, ['merchant', 'paid to', 'payee', 'description', 'note']);
  const typeIdx = pickIdx(headerMap, ['type', 'transaction type', 'direction', 'dr/cr']);
  const txs: BankTransaction[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.length < 2) continue;
    const date = parseDate(row[dateIdx] ?? '');
    const amtRaw = row[amtIdx] ?? '';
    const amtVal = Number(amtRaw.replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(amtVal) || amtVal === 0) continue;
    const merchant = (row[merchIdx] ?? '').trim() || null;
    const type = (row[typeIdx] ?? '').toLowerCase();
    const direction: BankTransaction['direction'] = /credit|received|in|incoming/.test(type) || amtVal < 0 ? 'credit' : 'debit';
    txs.push({
      txId: `upi_${provider}_${r}`,
      accountId: accountId(`acct_upi_${provider}`),
      amountPaise: paise(Math.abs(amtVal) * 100),
      direction,
      narration: merchant ?? `UPI ${provider}`,
      merchant,
      category: null,
      bookedAt: date as ReturnType<typeof isoTs>,
      mode: 'upi',
    });
  }
  return { provider, transactions: txs };
}

function pickIdx(map: Record<string, number>, keys: readonly string[]): number {
  for (const k of keys) if (map[k] !== undefined) return map[k] as number;
  return 0;
}

function mockUpi(): readonly BankTransaction[] {
  const now = Date.now();
  const merchants = ['Swiggy Instamart', 'Blinkit', 'Zomato', 'Uber India', 'Amazon Pay', 'BookMyShow', 'Apollo Pharmacy'];
  return merchants.map((m, i) => ({
    txId: `upi_mock_${i}`,
    accountId: accountId('acct_upi_gpay'),
    amountPaise: paise(50000 + i * 12000 + Math.round(Math.random() * 80000)),
    direction: 'debit' as const,
    narration: `UPI/${m}/payment`,
    merchant: m,
    category: null,
    bookedAt: new Date(now - i * 86_400_000).toISOString() as ReturnType<typeof isoTs>,
    mode: 'upi' as const,
  }));
}

export const upiExportParser: Connector = {
  id: 'upi-export-parser',
  displayName: 'GPay / PhonePe / Paytm CSV upload',
  category: 'upi',
  defaultMode: 'mock',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: 'mock',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.importedCount,
      disabledReason: null,
    };
  },

  async connect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    logConnectorConsent(ctx.dpdpLog, 'connector-upi-export-upload', 'GPay+PhonePe+Paytm CSV/PDF');
    const connectedAt = isoTs();
    writeStatus({ connected: true, lastSync: connectedAt, importedCount: 0 });
    return ok({ connectedAt });
  },

  async import(_ctx: ConnectorContext, opts?: { readonly fileBlob?: Blob }): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    if (opts?.fileBlob) {
      const t = await opts.fileBlob.text();
      if (t.includes(',') && t.includes('\n')) {
        const parsed = parseUpiCsv(t);
        if (parsed.transactions.length > 0) {
          writeStatus({ ...s, lastSync: isoTs(), importedCount: parsed.transactions.length });
          return ok({ transactions: parsed.transactions });
        }
      }
      const bridge = getPdfBridge();
      const extract = await bridge.extract({ blob: opts.fileBlob });
      const parsed = parseUpiCsv(extract.textFull);
      if (parsed.transactions.length > 0) {
        writeStatus({ ...s, lastSync: isoTs(), importedCount: parsed.transactions.length });
        return ok({ transactions: parsed.transactions });
      }
    }
    const mock = mockUpi();
    writeStatus({ ...s, lastSync: isoTs(), importedCount: mock.length });
    return ok({ transactions: mock });
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, lastSync: null, importedCount: 0 });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-upi-export-upload' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { transactions: mockUpi() };
  },
};
